/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Clock,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  LogIn,
  BarChart3,
} from 'lucide-react';
import {
  useSuperAdminSupportGrants,
  useSuperAdminSupportStats,
  useEnterSupportSession,
  useRevokeSupportGrant,
} from '@/hooks/api/useSuperAdminSupportAccess';
import { useSupportSessionStore } from '@/stores/supportSessionStore';
import { useNotificationStore } from '@/stores/notificationStore';
import TablePagination from '@/modules/venta/components/TablePagination';
import type { AccessLevel, GrantStatus, SupportAccessGrant } from '@/types/support-access';

export default function SuperAdminSupportAccessPage() {
  const { t } = useTranslation('platform');
  const { addNotification } = useNotificationStore();
  const { enterSession: storeEnterSession } = useSupportSessionStore();

  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const { data: stats } = useSuperAdminSupportStats();
  const { data: grantsData, isLoading } = useSuperAdminSupportGrants(statusFilter, page, pageSize);
  const enterMutation = useEnterSupportSession();
  const revokeMutation = useRevokeSupportGrant();

  const handleEnterSession = (grant: SupportAccessGrant) => {
    enterMutation.mutate(grant.id, {
      onSuccess: () => {
        storeEnterSession({
          grantId: grant.id,
          orgId: grant.orgId,
          orgName: grant.orgName,
          accessLevel: grant.accessLevel,
          expiresAt: grant.expiresAt,
        });
        addNotification({
          type: 'success',
          title: t('supportAccess.sessionStarted'),
          message: t('supportAccess.sessionStartedDesc', { orgName: grant.orgName }),
        });
      },
    });
  };

  const handleRevoke = (grantId: string) => {
    revokeMutation.mutate({ id: grantId }, {
      onSuccess: () => {
        addNotification({ type: 'success', title: t('supportAccess.revokeSuccess'), message: '' });
      },
    });
  };

  const getStatusBadge = (status: GrantStatus) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="ssa-badge ssa-badge--active"><CheckCircle2 size={12} /> {t('supportAccess.status.active')}</span>;
      case 'REVOKED':
        return <span className="ssa-badge ssa-badge--revoked"><XCircle size={12} /> {t('supportAccess.status.revoked')}</span>;
      case 'EXPIRED':
        return <span className="ssa-badge ssa-badge--expired"><Clock size={12} /> {t('supportAccess.status.expired')}</span>;
    }
  };

  const getAccessBadge = (level: AccessLevel) => {
    return level === 'READ_ONLY'
      ? <span className="ssa-badge ssa-badge--readonly"><Eye size={12} /> {t('supportAccess.readOnly')}</span>
      : <span className="ssa-badge ssa-badge--fullaccess"><EyeOff size={12} /> {t('supportAccess.fullAccess')}</span>;
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString();

  const getTimeRemaining = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return t('supportAccess.expired');
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  const statusOptions = [
    { value: undefined, label: t('supportAccess.filter.all') },
    { value: 'ACTIVE', label: t('supportAccess.status.active') },
    { value: 'REVOKED', label: t('supportAccess.status.revoked') },
    { value: 'EXPIRED', label: t('supportAccess.status.expired') },
  ];

  return (
    <div className="ssa-page">
      <div className="ssa-header">
        <div>
          <h1 className="ssa-title"><Shield size={22} /> {t('supportAccess.superadmin.title')}</h1>
          <p className="ssa-subtitle">{t('supportAccess.superadmin.subtitle')}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="ssa-stats-row">
        <div className="ssa-stat-card">
          <div className="ssa-stat-icon ssa-stat-icon--active"><Shield size={18} /></div>
          <div>
            <div className="ssa-stat-value">{stats?.activeCount ?? 0}</div>
            <div className="ssa-stat-label">{t('supportAccess.stats.activeGrants')}</div>
          </div>
        </div>
        <div className="ssa-stat-card">
          <div className="ssa-stat-icon ssa-stat-icon--total"><BarChart3 size={18} /></div>
          <div>
            <div className="ssa-stat-value">{stats?.totalGrantsToday ?? 0}</div>
            <div className="ssa-stat-label">{t('supportAccess.stats.grantsToday')}</div>
          </div>
        </div>
      </div>

      {/* Grants Table */}
      <div className="ssa-card">
        <div className="ssa-card-header">
          <h2>{t('supportAccess.superadmin.grants')}</h2>
          <div className="ssa-filter">
            {statusOptions.map((opt) => (
              <button
                key={opt.value || 'all'}
                className={`ssa-filter-btn ${statusFilter === opt.value ? 'ssa-filter-btn--active' : ''}`}
                onClick={() => { setStatusFilter(opt.value); setPage(0); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="ssa-card-body">
          {isLoading ? (
            <div className="ssa-spinner" />
          ) : !grantsData?.content?.length ? (
            <p className="ssa-empty">{t('supportAccess.noGrants')}</p>
          ) : (
            <>
              <div className="ssa-table-wrap">
                <table className="ssa-table">
                  <thead>
                    <tr>
                      <th>{t('supportAccess.orgName')}</th>
                      <th>{t('supportAccess.accessLevel')}</th>
                      <th>{t('supportAccess.grantedBy')}</th>
                      <th>{t('supportAccess.expires')}</th>
                      <th>{t('supportAccess.lastAccessed')}</th>
                      <th>{t('supportAccess.statusLabel')}</th>
                      <th>{t('supportAccess.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grantsData.content.map((grant) => (
                      <tr key={grant.id}>
                        <td className="ssa-cell-org">{grant.orgName}</td>
                        <td>{getAccessBadge(grant.accessLevel)}</td>
                        <td>{grant.grantedByName || grant.grantedByEmail}</td>
                        <td>
                          {grant.status === 'ACTIVE' ? (
                            <span className="ssa-countdown">{getTimeRemaining(grant.expiresAt)}</span>
                          ) : (
                            formatDate(grant.expiresAt)
                          )}
                        </td>
                        <td>
                          {grant.lastAccessedAt ? formatDate(grant.lastAccessedAt) : '—'}
                        </td>
                        <td>{getStatusBadge(grant.status)}</td>
                        <td>
                          {grant.status === 'ACTIVE' && (
                            <div className="ssa-actions">
                              <button
                                className="ssa-action-btn ssa-action-btn--enter"
                                onClick={() => handleEnterSession(grant)}
                                disabled={enterMutation.isPending}
                                title={t('supportAccess.enterSession')}
                              >
                                <LogIn size={14} /> {t('supportAccess.enter')}
                              </button>
                              <button
                                className="ssa-action-btn ssa-action-btn--revoke"
                                onClick={() => handleRevoke(grant.id)}
                                disabled={revokeMutation.isPending}
                                title={t('supportAccess.revokeAccess')}
                              >
                                <XCircle size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalElements={grantsData.totalElements}
                totalPages={grantsData.totalPages}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
              />
            </>
          )}
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .ssa-page {
    max-width: 1100px;
  }

  .ssa-header {
    margin-bottom: 24px;
  }

  .ssa-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 20px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0 0 4px;
  }

  .ssa-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
    margin: 0;
  }

  /* Stats */
  .ssa-stats-row {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
  }

  .ssa-stat-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 10px;
    flex: 1;
  }

  .ssa-stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ssa-stat-icon--active {
    background: rgba(34, 197, 94, 0.15);
    color: #16a34a;
  }

  .ssa-stat-icon--total {
    background: rgba(59, 130, 246, 0.15);
    color: #2563eb;
  }

  .ssa-stat-value {
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .ssa-stat-label {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  /* Card */
  .ssa-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 10px;
    overflow: hidden;
  }

  .ssa-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-wrap: wrap;
    gap: 12px;
  }

  .ssa-card-header h2 {
    font-size: 15px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }

  .ssa-card-body {
    padding: 20px;
  }

  /* Filter */
  .ssa-filter {
    display: flex;
    gap: 4px;
    background: var(--color-bg-primary);
    border-radius: 6px;
    padding: 2px;
  }

  .ssa-filter-btn {
    padding: 5px 12px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--color-text-secondary);
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .ssa-filter-btn:hover {
    color: var(--color-text-primary);
  }

  .ssa-filter-btn--active {
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  /* Badges */
  .ssa-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .ssa-badge--active { background: rgba(34, 197, 94, 0.15); color: #16a34a; }
  .ssa-badge--revoked { background: rgba(239, 68, 68, 0.15); color: #dc2626; }
  .ssa-badge--expired { background: rgba(156, 163, 175, 0.2); color: var(--color-text-muted); }
  .ssa-badge--readonly { background: rgba(59, 130, 246, 0.15); color: #2563eb; }
  .ssa-badge--fullaccess { background: rgba(245, 158, 11, 0.15); color: #d97706; }

  /* Table */
  .ssa-table-wrap {
    overflow-x: auto;
  }

  .ssa-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .ssa-table th {
    text-align: left;
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .ssa-table td {
    padding: 10px 12px;
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
    white-space: nowrap;
  }

  .ssa-table tr:hover td {
    background: var(--color-bg-hover);
  }

  .ssa-cell-org {
    font-weight: 500;
  }

  .ssa-countdown {
    font-family: var(--font-mono, monospace);
    font-weight: 600;
    color: var(--color-brand);
  }

  /* Actions */
  .ssa-actions {
    display: flex;
    gap: 6px;
  }

  .ssa-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border: none;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }

  .ssa-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ssa-action-btn--enter {
    background: rgba(34, 197, 94, 0.1);
    color: #16a34a;
  }

  .ssa-action-btn--enter:hover:not(:disabled) {
    background: rgba(34, 197, 94, 0.2);
  }

  .ssa-action-btn--revoke {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
    padding: 5px 7px;
  }

  .ssa-action-btn--revoke:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.2);
  }

  .ssa-empty {
    text-align: center;
    color: var(--color-text-muted);
    font-size: 13px;
    padding: 32px;
  }

  .ssa-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--color-border-subtle);
    border-top-color: var(--color-brand);
    border-radius: 50%;
    animation: ssa-spin 0.6s linear infinite;
    margin: 32px auto;
  }

  @keyframes ssa-spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 600px) {
    .ssa-stats-row { flex-direction: column; }
    .ssa-card-header { flex-direction: column; align-items: flex-start; }
  }
`;
